import {
  collection,
  addDoc,
  query,
  where,
  serverTimestamp,
  onSnapshot,
  updateDoc,
  doc,
  getDocs,
  limit,
  deleteDoc
} from 'firebase/firestore';
import { db } from '../config/firebase';

const NOTIFICATIONS_COLLECTION = 'notifications';

/**
 * Create a notification for a user
 * @param {string} userId - The user ID to notify
 * @param {string} type - The type of notification ('application_received' or 'application_accepted')
 * @param {string} message - The notification message
 * @param {object} metadata - Additional data (raceCardId, applicationId, etc.)
 */
export const createNotification = async (userId, type, message, metadata = {}) => {
  try {
    const notificationData = {
      userId,
      type,
      message,
      read: false,
      ...metadata,
      createdAt: serverTimestamp()
    };

    const docRef = await addDoc(collection(db, NOTIFICATIONS_COLLECTION), notificationData);
    return { id: docRef.id, ...notificationData };
  } catch (error) {
    console.error('Error creating notification:', error);
    throw error;
  }
};

/**
 * Get notifications for a user with real-time updates
 * @param {string} userId - The user ID
 * @param {function} callback - Callback function to receive notifications
 * @returns {function} Unsubscribe function
 */
export const getUserNotifications = (userId, callback) => {
  try {
    // Query without orderBy to avoid index requirement - we'll sort client-side
    const q = query(
      collection(db, NOTIFICATIONS_COLLECTION),
      where('userId', '==', userId),
      limit(50)
    );

    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        try {
          const notifications = querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          // Sort by createdAt descending (newest first)
          notifications.sort((a, b) => {
            try {
              const aTime = a.createdAt?.toDate?.() || 
                           (a.createdAt?.seconds ? new Date(a.createdAt.seconds * 1000) : null) || 
                           new Date(a.createdAt || 0);
              const bTime = b.createdAt?.toDate?.() || 
                           (b.createdAt?.seconds ? new Date(b.createdAt.seconds * 1000) : null) || 
                           new Date(b.createdAt || 0);
              return bTime.getTime() - aTime.getTime();
            } catch (sortError) {
              console.error('Error sorting notifications:', sortError);
              return 0;
            }
          });
          callback(notifications);
        } catch (error) {
          console.error('Error processing notifications:', error);
          callback([]);
        }
      },
      (error) => {
        console.error('Error in notifications listener:', error);
        console.error('Error code:', error.code);
        console.error('Error message:', error.message);
        callback([]);
      }
    );

    return unsubscribe;
  } catch (error) {
    console.error('Error setting up notifications listener:', error);
    callback([]);
    return () => {};
  }
};

/**
 * Get unread notification count for a user
 * @param {string} userId - The user ID
 * @param {function} callback - Callback function to receive count
 * @returns {function} Unsubscribe function
 */
export const getUnreadNotificationCount = (userId, callback) => {
  try {
    const q = query(
      collection(db, NOTIFICATIONS_COLLECTION),
      where('userId', '==', userId),
      where('read', '==', false)
    );

    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        callback(querySnapshot.size);
      },
      (error) => {
        console.error('Error in unread count listener:', error);
        callback(0);
      }
    );

    return unsubscribe;
  } catch (error) {
    console.error('Error setting up unread count listener:', error);
    callback(0);
    return () => {};
  }
};

/**
 * Mark a notification as read
 * @param {string} notificationId - The notification ID
 */
export const markNotificationAsRead = async (notificationId) => {
  try {
    const notificationRef = doc(db, NOTIFICATIONS_COLLECTION, notificationId);
    await updateDoc(notificationRef, {
      read: true,
      readAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error marking notification as read:', error);
    throw error;
  }
};

/**
 * Mark all notifications as read for a user
 * @param {string} userId - The user ID
 */
export const markAllNotificationsAsRead = async (userId) => {
  try {
    const q = query(
      collection(db, NOTIFICATIONS_COLLECTION),
      where('userId', '==', userId),
      where('read', '==', false)
    );
    
    const snapshot = await getDocs(q);
    const updatePromises = snapshot.docs.map(doc => {
      const notificationRef = doc.ref;
      return updateDoc(notificationRef, {
        read: true,
        readAt: serverTimestamp()
      });
    });

    await Promise.all(updatePromises);
  } catch (error) {
    console.error('Error marking all notifications as read:', error);
    throw error;
  }
};

/**
 * Delete old notifications, keeping only the most recent ones
 * @param {string} userId - The user ID
 * @param {number} keepCount - Number of recent notifications to keep (default: 50)
 */
export const deleteOldNotifications = async (userId, keepCount = 50) => {
  try {
    // Get all notifications for the user
    const q = query(
      collection(db, NOTIFICATIONS_COLLECTION),
      where('userId', '==', userId)
    );
    
    const snapshot = await getDocs(q);
    const allNotifications = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // Sort by createdAt (newest first)
    allNotifications.sort((a, b) => {
      try {
        const aTime = a.createdAt?.toDate?.() || 
                     (a.createdAt?.seconds ? new Date(a.createdAt.seconds * 1000) : null) || 
                     new Date(a.createdAt || 0);
        const bTime = b.createdAt?.toDate?.() || 
                     (b.createdAt?.seconds ? new Date(b.createdAt.seconds * 1000) : null) || 
                     new Date(b.createdAt || 0);
        return bTime.getTime() - aTime.getTime();
      } catch (sortError) {
        return 0;
      }
    });

    // If there are 50 or fewer, delete all of them. Otherwise, keep the 50 most recent.
    let notificationsToDelete;
    if (allNotifications.length <= keepCount) {
      // Delete all notifications if there are 50 or fewer
      notificationsToDelete = allNotifications;
    } else {
      // Keep only the most recent ones
      notificationsToDelete = allNotifications.slice(keepCount);
    }
    
    if (notificationsToDelete.length === 0) {
      return { deleted: 0 };
    }

    // Delete old notifications
    const deletePromises = notificationsToDelete.map(notif => 
      deleteDoc(doc(db, NOTIFICATIONS_COLLECTION, notif.id))
    );

    await Promise.all(deletePromises);
    return { deleted: notificationsToDelete.length };
  } catch (error) {
    console.error('Error deleting old notifications:', error);
    throw error;
  }
};

